/* eslint no-throw-literal: 0, no-param-reassign: 0 */
// TODO: [zhbliu] [#1402527] remove param 'cast' passing to JSData

import _ from '@bingads-webui-component-legacy/extension/underscore';
import DataStore from '@bingads-webui-component-legacy/js-data-campaign/index';
import edmCampaign from '@bingads-webui-component-legacy/edm-campaign/index';
import BaseRepository from '@bingads-webui-campaign-legacy/adextension-shared/service/repos/base';
import CampaignAssociationsRequestFactory from '@bingads-webui-campaign-legacy/adextension-shared/service/repos/campaignAssociationsRequestFactory';
import AdGroupAssociationsRequestFactory from '@bingads-webui-campaign-legacy/adextension-shared/service/repos/adgroupAssociationsRequestFactory';
import AccountAssociationsRequestFactory from '@bingads-webui-campaign-legacy/adextension-shared/service/repos/accountAssociationsRequestFactory';
import sharedDataModelUtil from '@bingads-webui-campaign-legacy/adextension-shared/util/sharedDataModelConvert';

export default class AssociationsRepository extends BaseRepository {
  constructor(options) {
    super(options);
    this._schema = options.schema;
    this.dataFormatter = options.associationsDataFormatter;
    this.afterChangeComitted = options.afterChangeComitted;
    this.requestFactory = new CampaignAssociationsRequestFactory();
  }

  createRequestFactory(entityType) {
    if (entityType === 'adgroup') {
      this.requestFactory = new AdGroupAssociationsRequestFactory();
    } else if (entityType === 'campaign') {
      this.requestFactory = new CampaignAssociationsRequestFactory();
    } else {
      this.requestFactory = new AccountAssociationsRequestFactory();
    }
  }

  processBatchResponse(associationArray, statusArr) {
    const extractIndexRegex = /^adExtensionAssociations\[(\d+)]/;
    let errors = _.chain(statusArr)
      .reject((item) => {
        return item.status === 200 || item.status === 204;
      })
      .pluck('data')
      .pluck('value')
      .flatten()
      .value();

    /* The associationsMap maps each error's property to the
    *  ad extension, campaign and adgroup that the error is for.
    *  This can be used to show the error together with the association
    *  that threw it.
    */
    let associationsMap = {},
      associations = {};

    _.each(errors, (error) => {
      if (error.Property && _.isString(error.Property)) {
        let index = _.last(error.Property.match(extractIndexRegex));

        if (index && Number(index) < associationArray.length) {
          associationsMap[error.Property] = associationArray[Number(index)];
        }
      }
    });

    associations.value = associationsMap;
    associations.failCount = _.uniq(_.values(associationsMap)).length;
    associations.successCount = associationArray.length - associations.failCount;

    if (!_.isEmpty(errors)) {
      throw {
        errors,
        type: 'odata',
        associations,
      };
    }
  }

  fetchLegacyImagesAsNeeded(data) {
    const imageAdExtensions = _.map(data, (item) => item.AdExtension);

    return sharedDataModelUtil.migrateLegacyDataAsync(imageAdExtensions).then(() => {
      return data;
    });
  }

  scopeParams(params) {
    return {
      campaignId: _.get(params, 'where.campaignId'),
      adGroupId: _.get(params, 'where.adGroupId'),
    };
  }

  findAll(params) {
    let scope = this.scopeParams(params),
      options = {
        entitySet: edmCampaign.currentAccount,
        cacheResponse: false,
      };

    this.requestFactory.setEntityIds(options, scope);

    options.entitySet = options.entitySet.AdExtensionAssociations;
    options.headers = _.extend(options.headers || {},
      { 'x-ms-refreshcampaigncache': 'true' });
    _.extend(options, { bypassCache: true });

    params = this.adaptParams(params);

    params.where = {
      'AdExtension.Status': {
        '$eq': 'Enum.AdExtensionStatus\'Active\'',
      },
    };

    // TODO: [zhbliu] [#1381268] talk to MT guys to remove unnessasary startdate and enddate in query
    //the magic date is for bidata, but useless for this request
    let magicDate = '9/30/2015';

    params.query = _.defaults({
      adextensiontype: this._schema.name,
      startdate: magicDate,
      enddate: magicDate,
    }, params.query);

    if (params.associationType) {
      params.query.adextassociationtype = params.associationType;
    }

    const extraImgExpand = this._schema.name === 'ImageAdExtension' ? '($expand=Model.ImageAdExtension/Images)' : '';

    params.expand = [`AdExtension${extraImgExpand}`];
    params.limit = 25; // Refer to MaxSelectedImagesPerAdGroupOrCampaignForV2

    return DataStore.AdExtensionAssociation
      .findAll(params, options)
      .then((data) => {
        if (this._schema.name === 'ImageAdExtension') {
          return this.fetchLegacyImagesAsNeeded(data);
        }

        return data;
      })
      .then(this.dataFormatter);
  }

  destroyAll(params) {
    let scope = this.scopeParams(params),
      toRemove = _.get(params, 'where.id.in'),
      requests = this.requestFactory.generateDestroyRequest(toRemove, scope);

    return DataStore.AdExtensionAssociation
      .destroyAll({
        reqs: requests,
      }, {
        cast: 'Default.Disassociate',
        action: 'Post',
        cacheResponse: false,
        batchURL: this.batchURL,
      })
      .tap(this.afterChangeComitted)
      .tap(_.partial(this.processBatchResponse, requests[0].adExtensionAssociations));
  }

  destroyAllByOdata(params) {
    let adExtensionAssociations = [];

    _.each(params, (param) => {
      const scope = this.scopeParams(param);
      const toRemove = _.get(param, 'where.id.in');
      const request = this.requestFactory.generateDestroyRequest(toRemove, scope);

      adExtensionAssociations = adExtensionAssociations.concat(request[0].adExtensionAssociations);
    });


    return DataStore.AdExtensionAssociation
      .create({
        adExtensionAssociations,
      }, {
        cast: 'Default.Disassociate',
        action: 'Post',
        cacheResponse: false,
      })
      .tap(this.afterChangeComitted);
  }

  updateAll(attrs, params, scopes = [this.scopeParams(params)]) {
    return this._updateAll(attrs, params, scopes);
  }

  _updateAll(attrs, params, _scopes) {
    let scopes = _.map(_scopes, (scope) => _.pick(scope, 'campaignId', 'adGroupId')),
      toAdd = _.get(params, 'where.id.in'),
      requests = this.requestFactory.generateUpdateRequest(toAdd, scopes);

    return DataStore.AdExtensionAssociation
      .updateAll(attrs, {
        reqs: requests,
      }, {
        cast: 'Default.Associate',
        action: 'Post',
        cacheResponse: false,
        batchURL: this.batchURL,
      })
      .tap(this.afterChangeComitted)
      .tap(_.partial(this.processBatchResponse, requests[0].adExtensionAssociations));
  }
}
